import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, queryAsync, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import {
  handleWatchlistChangeButtonClick,
  overlayOpenListener,
  renderAccordionHeader,
  renderButtonGroup,
  renderLastScannedOn,
  renderLink,
  renderSideMenuCompanyIfNeeded,
  renderSideMenuOperatingSystems,
  renderSideMenuRiskScore,
  renderSideMenuSectionTitle,
  renderSideMenuState,
  renderSideMenuStatus,
  renderSideMenuWatchlist,
  RiskManagementSideMenuEvents,
  SideMenuButton,
} from '../../common/common';
import { translateFn } from '../../../../utils/translator.helper';
import { DEVICES_CLASS, LocalIdentitiesType, RiskTypes, State } from '../../../common/constants';
import { getBulletColorForScore, getWatchlistButtonTextBasedOnState } from '../../../common/utils';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { DropDownSelectEvents } from '../types';
import { UserType } from '../../../usersApp/types';
import { WEB_COMPONENT_GZ_TAB_GROUP } from '../../../../webcomponents.constants';
import { GzTabGroupWebComponent } from '../../../../ui/gzTabs/gzTabGroup';
import _ from 'lodash';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { UserExtraDetails } from '../../components/userDetails/types/types';
import { ContextMenuOverlayElement } from '../../../../ui/gzContextMenuOverlay';
import { ImageState } from '../../../../ui/gzImageRepo/types';

/**
 * @summary Type here the description of this component
 * @tag users-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('users-side-menu')
export class UsersSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  item = {} as any;

  @property({ type: Object, attribute: false })
  indicatorsDetails = null as null | Array<any>;

  @property({ type: Object, attribute: false })
  devicesDetails = null as null | Array<any>;

  @property({ type: Boolean, attribute: false })
  showIgnoredRisks = true;

  @property({ type: Object, attribute: false })
  userDetails = null as null | any;

  @property({ type: Boolean, attribute: false })
  userDetailsHasErrors = false;

  @property({ type: Object, attribute: false })
  userExtraDetails = null as null | UserExtraDetails;

  @property({ type: Boolean, attribute: true, reflect: true })
  showExtraDetailsPanel = false;

  @property({ type: String, attribute: false })
  extraDetailsPanelTitle = null as string | null;

  @query(WEB_COMPONENT_GZ_TAB_GROUP)
  private readonly tabGroupComponent!: GzTabGroupWebComponent;

  @query('#watchlist-button')
  private readonly watchlistButton!: GzButtonWebComponent;

  @query('#extra-details-context-menu-side-panel')
  private readonly overlay!: ContextMenuOverlayElement;

  riskType = RiskTypes.USERS;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    return html` ${this.renderSideMenuHeader()} ${this.renderSideMenuContent()} `;
  }

  private renderSideMenuHeader() {
    if (this.showExtraDetailsPanel) {
      return html`${this.renderExtraDetailsPanelHeader()}`;
    }

    return html` <div class="header mt-6">
      <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2"> ${this.item.username}</gz-title>
    </div>`;
  }

  private renderSideMenuContent() {
    if (this.showExtraDetailsPanel) {
      return html`
        <user-extra-details
          .itemsType="${this.userExtraDetails?.itemsType}"
          .items="${this.userExtraDetails?.items}"
          .companyId="${this.userExtraDetails?.companyId}"
        >
        </user-extra-details>
      `;
    }

    return html`
      <div class="content">
        ${this.renderGeneralSection()} ${this.renderMitigationDetailsSection()}
        <div class="section" data-section="tabs">${this.renderTabs()}</div>
      </div>
      <gz-context-menu-overlay id="extra-details-context-menu-side-panel" @openContextMenu="${overlayOpenListener}">
      </gz-context-menu-overlay>
    `;
  }

  updated(changedProperties): void {
    // We should rerender tabGroup component if tab name changed, because if we change only the name of the tab,
    // it does not rerender the tab group automatically
    if (
      changedProperties.has('showIgnoredRisks') &&
      changedProperties.get('showIgnoredRisks') !== undefined &&
      this.tabGroupComponent
    ) {
      this.tabGroupComponent.requestUpdate();
    }
  }

  public changeWatchlistButtonState(isLoading: boolean) {
    this.watchlistButton.setLoadingState(isLoading);
  }

  private renderGeneralSection(): TemplateResult {
    const buttons: Array<SideMenuButton> = [
      {
        text: translateFn('View risks'),
        handler: this.handleViewRisksButtonClick,
        id: 'view-risks-button',
      },
      {
        text: getWatchlistButtonTextBasedOnState(this.item.watchlist),
        handler: handleWatchlistChangeButtonClick.bind(this),
        id: 'watchlist-button',
      },
      {
        text: translateFn('View events and alerts'),
        handler: this.handleViewEventsAndAlertsButtonClick,
        id: 'view-events-and-alerts-button',
        disabled: !this.item.company?.specifics?.features.edrUsable,
        tooltiptext: translateFn('This option requires a license that includes the Historical Search feature.'),
      },
      {
        text: translateFn('View incidents'),
        handler: this.handleViewIncidentsButtonClick,
        id: 'view-incidents-button',
        disabled:
          !this.item.company?.specifics?.features.edrVisible && !this.item.company?.specifics?.features.eliteVisible,
        tooltiptext: translateFn('This option requires a license that includes the EDR feature.'),
      },
    ];

    return html`
      <div class="section pb-2" data-section="general">
        ${renderSideMenuSectionTitle(translateFn('General'))}
        ${renderSideMenuRiskScore(this.item, this.overlay, this.riskType)}
        ${this.renderIdentityType(this.item.userType)} ${this.renderDepartment(this.item.department)}
        ${this.renderIdentityResources()}
        <gz-labeled-content label="${translateFn('Email')}">
          ${!this.item.email || this.item.email === '' ? '-' : this.item.email}</gz-labeled-content
        >
        ${this.renderResourceId(this.item.resourceId)}
        ${renderSideMenuOperatingSystems(this.item.operatingSystems ?? [])} ${this.renderRegion(this.item.region)}
        ${renderLastScannedOn(this.item.lastScan)} ${renderSideMenuCompanyIfNeeded(this.item.company.name)}
        ${renderSideMenuStatus(this.item.status)} ${renderSideMenuState(this.item.state)}
        ${renderSideMenuWatchlist(this.item.watchlist)} ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private renderMitigationDetailsSection(): TemplateResult {
    const buttons: Array<SideMenuButton> = [];
    if (this.item.state === State.IGNORED) {
      buttons.push({
        text: translateFn('Restore ignored identity'),
        handler: this.handleRestoreButtonClick,
        id: 'restore-user-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    } else {
      buttons.push({
        text: translateFn('Ignore identity'),
        handler: this.handleIgnoreButtonClick,
        id: 'ignore-user-button',
        disabled: !this.item.company.specifics.features.iorUsable,
        tooltiptext: translateFn('Your current license does not provide access to this feature.'),
      });
    }

    return html`
      <div class="section pb-2 pt-1 no-border" data-section="mitigation">
        ${renderSideMenuSectionTitle(translateFn('Risk mitigation'))} ${renderButtonGroup(buttons)}
      </div>
    `;
  }

  private renderTabs(): TemplateResult {
    if (!this.indicatorsDetails) {
      return html` <gz-loader></gz-loader>`;
    }

    return html` <gz-tab-group>
      <gz-tab id="localAdTab" label="${translateFn('Identity risks')}">
        <gz-toggle class="pb-1" ?checked="${this.showIgnoredRisks}" @toggleChange="${this.handleChange}">
          ${translateFn('Show ignored risks')}
        </gz-toggle>
        ${this.renderIndicators(this.indicatorsDetails)}
      </gz-tab>
    </gz-tab-group>`;
  }

  private renderDevices(): TemplateResult {
    if (!this.devicesDetails) {
      return html` <gz-loader></gz-loader>`;
    }

    if (this.devicesDetails.length === 0) {
      return html` <div class="d-flex flex-row">
        <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${translateFn('N/A')}</gz-text>
      </div>`;
    }

    const result: any = [];

    const options = [
      {
        title: translateFn('View incidents'),
        value: DropDownSelectEvents.viewIncidents,
        disabled:
          !this.item.company?.specifics?.features.edrVisible && !this.item.company?.specifics?.features.eliteVisible,
      },
      {
        title: translateFn('View events and alerts'),
        value: DropDownSelectEvents.viewEvents,
        disabled: !this.item.company?.specifics?.features.edrUsable,
      },
    ];

    for (const device of this.devicesDetails) {
      const template: TemplateResult = html` <div class="d-flex flex-row align-items-center">
        <gz-icon-label icon="${DEVICES_CLASS.get(device.type)}"></gz-icon-label>
        <gz-tooltip class="flex-1" tooltipText="${device.name}" isEllipsis>
          <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}">${device.name}</gz-text>
        </gz-tooltip>
        <gz-select
          class="side-menu-select"
          options="${JSON.stringify(options)}"
          @selectionChanged=${e => this.handleDeviceSelectionChanged(e.detail.selected, device)}
        >
          <gz-icon-button
            icon="ds-icon-more"
            fontSize="24"
            buttonType="square"
            buttonFill="quiet"
            buttonColor="secondary"
          >
          </gz-icon-button>
        </gz-select>
      </div>`;

      result.push(template);
    }

    return result;
  }

  private handleChange(e): void {
    this.showIgnoredRisks = e.detail.checked;
  }

  private handleViewRisksButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_HUMAN_RISKS_BY_USERS, {
        detail: {
          companyId: this.item.company._id,
          users: [
            {
              username: this.item.username,
              uniqueIdentifier: this.item.uniqueIdentifier,
            },
          ],
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleRestoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.RESTORE_USERS, {
        detail: {
          companyId: this.item.company._id,
          usersIds: [this.item._id],
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleIgnoreButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.IGNORE_USERS, {
        detail: {
          companyId: this.item.company._id,
          usersIds: [this.item._id],
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleViewEventsAndAlertsButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_EVENTS_AND_ALERTS_BY_USER, {
        detail: {
          companyId: this.item.company._id,
          username: this.item.username,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private handleViewIncidentsButtonClick(): void {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_INCIDENTS_BY_USER, {
        detail: {
          companyId: this.item.company._id,
          username: this.item.username,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  handleDeviceSelectionChanged(selectedOption: DropDownSelectEvents, device) {
    switch (selectedOption) {
      case DropDownSelectEvents.viewIncidents:
        this.dispatchEvent(
          new CustomEvent(DropDownSelectEvents.viewIncidents, {
            detail: {
              endpointId: device.endpointId,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
          })
        );
        break;
      case DropDownSelectEvents.viewEvents:
        this.dispatchEvent(
          new CustomEvent(DropDownSelectEvents.viewEvents, {
            detail: {
              endpointId: device.endpointId,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
          })
        );
        break;
      default:
        break;
    }
  }

  private renderIndicators(indicators): TemplateResult {
    // No indicators at all
    if (indicators.length === 0) {
      return html`
        <gz-image-repo state="${ImageState.SUCCESS}">
          <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No risks found')}</gz-title>
          <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
            ${translateFn('There are no risks associated with this identity.')}
          </gz-text>
        </gz-image-repo>
      `;
    }

    if (!this.showIgnoredRisks) {
      const activeIndicators = indicators.filter((misconfiguration: any) => {
        return misconfiguration['state'] === State.ACTIVE;
      });

      // We have only ignored indicators and we hid them
      if (activeIndicators.length === 0) {
        return html`
          <gz-image-repo state="${ImageState.EMPTY}">
            <gz-title fontWeight="${FontWeights.MEDIUM}" fontSize='${FontSizes.HEADING_4}'>${translateFn('No results found')}</gz-title>
            <gz-text fontSize="13" fontWeight="${FontWeights.NORMAL}">
              ${translateFn('Adjust your filter selection to view the ignored risks.')}
            </gz-text>
          </gz-image-repo>
        `;
      }

      return this.renderIndicatorsList(activeIndicators);
    }

    // Clone here, because we will perform a sort below, and we want to keep original details unchanged
    return this.renderIndicatorsList(_.cloneDeep(indicators));
  }

  private renderIndicatorsList(indicators): TemplateResult {
    indicators.sort((item1, item2) => {
      if (item2.score < item1.score) {
        return -1;
      } else if (item2.score > item1.score) {
        return 1;
      }

      return 0;
    });

    const result: any = [];

    for (const indicatorItem of indicators) {
      let hasEmptyState = false;

      if (!indicatorItem.extraDetails) {
        hasEmptyState = true;
      } else if (Object.keys(indicatorItem.extraDetails).length === 0) {
        hasEmptyState = true;
      }

      const copyToClipboard = {
        enabled: true,
        text: indicatorItem.name,
      };

      const template: TemplateResult = html`
        <gz-accordion align="left" .copyToClipboard="${copyToClipboard}">
          ${renderAccordionHeader(indicatorItem['name'], indicatorItem['score'], indicatorItem['state'])}
          ${this.renderUserBehaviorRiskDetailsSection(indicatorItem)}
          ${this.renderUserDetailsSection(indicatorItem.extraDetails, hasEmptyState, indicatorItem.id)}
        </gz-accordion>
      `;

      result.push(template);
    }

    return html` ${renderSideMenuSectionTitle(translateFn('Identity risks') + ` (${indicators.length})`)} ${result} `;
  }

  private renderUserBehaviorRiskDetailsSection(indicatorItem) {
    return html` <div class="pb-1">
      <gz-text class="text-description pb-2 pt-1" fontSize="${FontSizes.PARAGRAPH_12}"
        >${unsafeHTML(indicatorItem.description)}
      </gz-text>
      ${this.renderSeverityIndicator(indicatorItem)}
      <gz-labeled-content label="${translateFn('State')}">
        ${BaseWebComponentTranslator.getInstance().getRiskManagementState(indicatorItem.state)}
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('Occurrences')}">
        ${indicatorItem.allOccurrences ?? 0} ${translateFn('since last scan')}
      </gz-labeled-content>
      <gz-labeled-content label="${translateFn('Risk mitigation')}"></gz-labeled-content>
      <gz-text class="text-description pb-1" fontSize="${FontSizes.PARAGRAPH_12}"
        >${unsafeHTML(indicatorItem.action)}
      </gz-text>
      <div class="d-flex flex-row align-items-center link-container pt-1">
        ${renderLink(translateFn('View risk details'), () => {
          this.handleViewRiskDetailsButtonClick(indicatorItem.id);
        })}
        ${renderLink(
          translateFn('View identities'),
          () => {
            this.handleViewOtherUsersButtonClick(indicatorItem.id);
          },
          true
        )}
      </div>
    </div>`;
  }

  private renderUserDetailsSection(userExtraDetails, hasEmptyState, indicatorId) {
    if (hasEmptyState) {
      return nothing;
    }
    return html` <div class="section pb-2 pt-1" data-section="userDetails">
      <user-details
        id="user-details-component"
        .userInformation="${userExtraDetails}"
        .extraDetailsPanelTitle="${Application.modules.riskManagement.controller.helpers.routinesTranslations.translateRoutineName(
          indicatorId
        )}"
        .companyId="${this.item.company._id}"
        ?hasErrors="${this.userDetailsHasErrors}"
        ?emptyState="${hasEmptyState}"
      ></user-details>
    </div>`;
  }

  renderSeverityIndicator(indicatorItem) {
    if (typeof indicatorItem.score === 'undefined') {
      return nothing;
    }

    return renderSideMenuRiskScore(indicatorItem, this.overlay, RiskTypes.USER_BEHAVIOUR_RISKS);
  }

  handleViewOtherUsersButtonClick(indicatorId) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_USERS_BY_RISK, {
        detail: {
          companyId: this.item.company._id,
          indicatorId,
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  handleViewRiskDetailsButtonClick(indicatorId) {
    this.dispatchEvent(
      new CustomEvent(RiskManagementSideMenuEvents.VIEW_HUMAN_RISKS_BY_RISKS, {
        detail: {
          companyId: this.item.company._id,
          indicatorIds: [indicatorId],
        },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private handleUserExtraDetailsBackButtonClick() {
    this.showExtraDetailsPanel = false;
  }

  private renderExtraDetailsPanelHeader() {
    return html`
      <div class="header mt-2">
        <gz-button
          class="backBtn"
          type="quiet"
          color="secondary"
          buttonheight="small"
          buttonfontsize="small"
          @click="${this.handleUserExtraDetailsBackButtonClick}"
        >
          <gz-icon class="backBtn-icon" color="secondary" size="24" name="ds-icon-arrow_left_collapse"></gz-icon>
          ${translateFn('Back')}
        </gz-button>
        <gz-title fontSize="${FontSizes.HEADING_5}" class="mb-2 mt-3"> ${this.extraDetailsPanelTitle}</gz-title>
      </div>
    `;
  }

  private renderRegion(region: String) {
    return !region
      ? nothing
      : html` <gz-labeled-content label="${translateFn('Region')}">
          <div class="entities-container d-flex flex-row">
            <gz-tooltip isEllipsis tooltipText="${region}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
                >${region}
              </gz-text>
            </gz-tooltip>
          </div>
        </gz-labeled-content>`;
  }

  private renderIdentityType(userTypes) {
    if (!userTypes) {
      return '-';
    }

    const translator = BaseWebComponentTranslator.getInstance();
    return html`<gz-labeled-content label="${translateFn('Identity type')}">
      ${userTypes.map(userType => translator.getRiskManagementUserType(userType)).join(', ')}
    </gz-labeled-content>`;
  }

  private renderIdentityResources() {
    const userTypes = this?.item?.userType;
    let local = false;

    for (const type of userTypes) {
      if (LocalIdentitiesType[type]) {
        local = true;
      }
    }

    return local
      ? html`<gz-labeled-content class="devices-labeled-content" label="${translateFn('Identity resources')}">
          ${this.renderDevices()}
        </gz-labeled-content>`
      : nothing;
  }

  private renderResourceId(resourceId: string) {
    const resourceIdValue = !resourceId ? '-' : resourceId;

    let local = false;

    for (const type of this.item.userType) {
      if (LocalIdentitiesType[type]) {
        local = true;
      }
    }

    return local
      ? nothing
      : html` <gz-labeled-content label="${translateFn('Resource ID')}">
          <div class="entities-container d-flex flex-row">
            <gz-tooltip isEllipsis tooltipText="${resourceIdValue}">
              <gz-text class="d-inline" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
                >${resourceIdValue}
              </gz-text>
            </gz-tooltip>
          </div>
        </gz-labeled-content>`;
  }

  private renderDepartment(department) {
    if (!department || department === '') {
      department = translateFn('Unknown');
    }

    return html`<gz-labeled-content label="${translateFn('Department')}">
      <gz-tooltip isEllipsis class="w-100" tooltiptext="${department}">
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="d-inline">
          ${department}
        </gz-text>
      </gz-tooltip></gz-labeled-content
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'users-side-menu': UsersSideMenuWebComponent;
  }
}
